/**
 * Enumerados del contrato de flotas de taxi/VTC (docs/flotas-drivers/api-contract.md).
 * Los valores viajan tal cual al backend (snake_case); solo cambia su etiqueta
 * en pantalla (DRIVERS_FLEET.* en los vocabularios).
 */

export type FleetMode = 'taxi' | 'vtc';
export const FLEET_MODES: readonly FleetMode[] = ['taxi', 'vtc'];

/** Tipo de flota (Master Plan: emisora, taxista patrón, flota VTC). Decide el modo y el menú. */
export type FleetKind = 'dispatch_center' | 'owner_operator' | 'vtc_fleet';
export const FLEET_KINDS: readonly FleetKind[] = ['dispatch_center', 'owner_operator', 'vtc_fleet'];
export const FLEET_KIND_MODE: Record<FleetKind, FleetMode> = {
  dispatch_center: 'taxi',
  owner_operator: 'taxi',
  vtc_fleet: 'vtc',
};

export type FleetStatus = 'draft' | 'in_review' | 'rejected' | 'approved' | 'suspended';
export const FLEET_STATUSES: readonly FleetStatus[] = [
  'draft',
  'in_review',
  'rejected',
  'approved',
  'suspended',
];

export type DocumentStatus = 'pending' | 'in_review' | 'approved' | 'rejected';
export const DOCUMENT_STATUSES: readonly DocumentStatus[] = [
  'pending',
  'in_review',
  'approved',
  'rejected',
];

export type ExpiryStatus = 'none' | 'valid' | 'expiring_soon' | 'expired';

export type DocumentOwnerType = 'fleet' | 'vehicle' | 'driver';
export const DOCUMENT_OWNER_TYPES: readonly DocumentOwnerType[] = ['fleet', 'vehicle', 'driver'];

export type RejectionReason =
  | 'illegible'
  | 'expired'
  | 'holder_mismatch'
  | 'plate_mismatch'
  | 'wrong_document'
  | 'other';

export type DocumentType =
  | 'bank_ownership_certificate'
  | 'route_sheet_authorization'
  | 'circulation_permit'
  | 'technical_sheet_itv'
  | 'insurance_policy'
  | 'taxi_municipal_license'
  | 'vtc_transport_card'
  | 'identity'
  | 'driving_license'
  | 'taxi_driver_card'
  | 'vtc_driver_permit'
  | 'social_security_registration';

/** Pasos del alta de la flota: los datos de facturación y los documentos de empresa. */
export type FleetOnboardingStepKey =
  | 'billing_info'
  | 'bank_ownership_certificate'
  | 'route_sheet_authorization';

export type FleetMembershipStatus = 'pending' | 'accepted' | 'rejected' | 'removed';
/** `associate`: socio de una emisora, con licencia y coche propios (cobra íntegro). */
export type EmploymentType = 'salaried' | 'self_employed' | 'associate';
export type FleetRole = 'fleet_owner' | 'fleet_manager' | 'fleet_accountant';
export const FLEET_ROLES: readonly FleetRole[] = ['fleet_owner', 'fleet_manager', 'fleet_accountant'];

export type FleetVehicleStatus = 'incomplete' | 'in_review' | 'rejected' | 'approved';

export type ShiftSlot = 'morning' | 'afternoon' | 'night';
export const SHIFT_SLOTS: readonly ShiftSlot[] = ['morning', 'afternoon', 'night'];

/** 1 = lunes … 7 = domingo. */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export const WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5, 6, 7];

export type BillingEntityType = 'company' | 'self_employed';

export type SettlementMovementType = 'trip_earning' | 'driver_share' | 'payout' | 'adjustment';

export type FleetReport = 'trips' | 'drivers' | 'settlements' | 'driver_settlements' | 'route_sheets';
export type FleetReportFormat = 'csv' | 'pdf';

export type FleetMemberStatus = 'invited' | 'active';

export type ProfessionalDriverOnboardingStatus = 'in_progress' | 'in_review' | 'rejected' | 'approved';
export type ProfessionalDriverWorkingAs = 'own_license' | 'fleet_salaried' | 'fleet_self_employed';

/** Urgencia de una fila de la matriz de documentos, de más a menos urgente. */
export type DocumentUrgency = 'expired' | 'rejected' | 'expiring_soon' | 'in_review' | 'valid';

/** Por dónde llega un servicio. Solo `waiis_app` paga cuota al pasajero. */
export type DemandChannel = 'waiis_app' | 'emitter_phone' | 'emitter_web' | 'stand' | 'street_hail';
export const DEMAND_CHANNELS: readonly DemandChannel[] = ['waiis_app', 'emitter_phone', 'emitter_web', 'stand', 'street_hail'];

export type ServiceType = 'immediate' | 'reservation' | 'airport_premium';
export const SERVICE_TYPES: readonly ServiceType[] = ['immediate', 'reservation', 'airport_premium'];

export type DispatchStrategy = 'proximity' | 'stand_order' | 'zone';
export const DISPATCH_STRATEGIES: readonly DispatchStrategy[] = ['proximity', 'stand_order', 'zone'];

export type ChallengeStatus = 'draft' | 'scheduled' | 'active' | 'finished' | 'cancelled';
export const CHALLENGE_STATUSES: readonly ChallengeStatus[] = ['draft', 'scheduled', 'active', 'finished', 'cancelled'];
export type ChallengeParticipantStatus = 'in_progress' | 'completed' | 'paid';

export type FeeSimulationReason = 'rule' | 'channel_exempt' | 'region_disabled' | 'no_rule';

/** Estado en vivo de un conductor y de un coche (mockup 28). */
export type DriverPresence = 'on_trip' | 'available' | 'offline';
export const DRIVER_PRESENCES: readonly DriverPresence[] = ['on_trip', 'available', 'offline'];
export type VehiclePresence = 'on_street' | 'parked';

export type RepresentativeRole = 'sole_admin' | 'joint_admin' | 'shareholder_25' | 'attorney';
export const REPRESENTATIVE_ROLES: readonly RepresentativeRole[] = ['sole_admin', 'joint_admin', 'shareholder_25', 'attorney'];

export type SettlementPeriod = 'week' | 'month';
export type DriverPayoutStatus = 'pending' | 'paid';
export type AttentionKind = 'document_expired' | 'document_rejected' | 'driver_without_vehicle' | 'driver_request';
